import { FlowSection } from './HelpDialog';

export interface HelpEntry {
  title: string;
  intro?: string;
  warning?: string;
  flows: FlowSection[];
  notes?: string[];
}

/**
 * Central registry of "How this works" help content, rendered by <PageHelp k="..." />.
 * Keep entries plain-language so any operator (not just engineers) can follow the
 * flow — the Atlassian-style step diagrams come from HelpDialog.
 */
export type HelpKey =
  | 'drivers'
  | 'users'
  | 'rides'
  | 'zones'
  | 'staff'
  | 'liveops'
  | 'reports'
  | 'audit'
  | 'notifications'
  | 'rideOptions'
  | 'marketing'
  | 'cityOps'
  | 'cityOpsContext'
  | 'cityOpsLaunchMode'
  | 'cityOpsForceOnline'
  | 'cityOpsBulk'
  | 'cityOpsPreview'
  | 'cityOpsInsights'
  | 'cityOpsTuning'
  | 'cityOpsReachTruth'
  | 'driverPayouts'
  | 'driverPayoutsExport'
  | 'driverPayoutsCoins'
  | 'driverPayoutsManage'
  | 'support';

export const HELP_CONTENT: Record<HelpKey, HelpEntry> = {
  drivers: {
    title: 'Driver Management — how it works',
    intro: 'Onboard, verify and manage drivers. A driver can only receive ride requests once their mandatory documents are approved.',
    flows: [
      {
        title: 'Approve a new driver',
        steps: [
          { label: 'Find the driver', detail: 'Use the Approval filter → "Ready to approve", or search by phone / plate / licence.' },
          { label: 'Open the approve modal', detail: 'Right-click the row → "Approve Driver".' },
          { label: 'Verify the 3 mandatory docs', detail: 'Check RC, Driving Licence and Profile photo — or tick "Verify all mandatory documents".' },
          { label: 'Approve', detail: 'The Approve button enables only when all docs are verified, T&C accepted and app permissions granted.' },
        ],
      },
      {
        title: 'Compliance documents',
        steps: [
          { label: 'Open compliance', detail: 'Right-click the row → "Compliance Docs".' },
          { label: 'Set statuses', detail: 'Insurance, PUC, State Permit and AITP — set each to Verified / Pending / Rejected.' },
          { label: 'Save', detail: 'Compliance is separate from the mandatory-doc approval above.' },
        ],
      },
    ],
    notes: [
      '"Ready to approve" means a driver has all 3 documents uploaded, accepted the Terms & Conditions, and granted every mandatory app permission — only the verification clicks are left. (Drivers still missing T&C or a permission won\'t appear here.)',
      'Mark a driver Available/Unavailable or Delete them from the same right-click menu.',
      'Region-scoped staff only see drivers in their assigned cities.',
      'Approving requires the drivers:approve permission; editing requires drivers:write.',
    ],
  },

  users: {
    title: 'User Management — how it works',
    intro: 'View and manage every rider, driver and staff account on the platform.',
    flows: [
      {
        title: 'Edit a user',
        steps: [
          { label: 'Open quick view', detail: 'Right-click the row → "Edit / Quick View".' },
          { label: 'Update fields', detail: 'Name, email, phone, verification flags, etc.' },
          { label: 'Save', detail: 'Or open the full details page for everything.' },
        ],
      },
      {
        title: 'Suspend or investigate',
        steps: [
          { label: 'Suspend', detail: 'Right-click → Suspend. The user loses app access until reactivated (reversible).' },
          { label: 'Investigate', detail: 'Right-click → Show Rides or Show Wallet to see their activity.' },
        ],
      },
    ],
    notes: [
      'Search by phone, name or email — results are server-side, so it scales to millions of users.',
      'Suspend is reversible; Delete is permanent.',
      'Editing requires the users:write permission.',
    ],
  },

  rides: {
    title: 'Rides — how it works',
    intro: 'Browse and inspect every ride across the platform.',
    flows: [
      {
        title: 'Find and inspect a ride',
        steps: [
          { label: 'Search / filter', detail: 'By status, or by rider/driver when you arrive from a user row.' },
          { label: 'Open the ride', detail: 'Click a row for the full ride detail, timeline and payment.' },
        ],
      },
    ],
    notes: [
      'This page is read-mostly — refunds are handled in Finance, live tracking in Live Ops.',
      'Region-scoped staff only see rides whose pickup/drop-off city is in their assigned regions.',
    ],
  },

  zones: {
    title: 'Zones, Surge & Pricing — full guide',
    intro:
      'A "zone" is a polygon drawn on the map inside a city. Every ride is matched to the zone its pickup falls in, and that zone decides the surge, the fares and even which map provider is used. Zones are organised as State → City → Zone.',
    warning:
      'The zone list stays empty until you pick a State and a City — this is a deliberate performance guard (a large platform can have thousands of zones).',
    flows: [
      {
        title: '1 · Find a zone (State → City → Zone)',
        steps: [
          { label: 'Pick a State', detail: 'The left-hand tree lists every state that has zones.' },
          { label: 'Pick a City', detail: 'Expand the state and choose one of its cities.' },
          { label: 'See its zones', detail: 'The right panel lists every zone in that city with its active status, surge, and how many pricing / subscription overrides it has.' },
        ],
      },
      {
        title: '2 · Create or edit a zone',
        steps: [
          { label: 'Add or open a zone', detail: 'Create a new zone, or click an existing one to edit it.' },
          { label: 'Set the basics', detail: 'Name, City & State, and Active on/off — an inactive zone is ignored by the app.' },
          { label: 'Set the shape', detail: 'A zone is a map polygon built from a centre point (latitude / longitude) plus a size.' },
        ],
      },
      {
        title: '3 · Surge & rush hour (edit from the LIST view)',
        steps: [
          { label: 'Edit from the list', detail: 'Surge is edited from the zone list — the zone detail page shows it read-only.' },
          { label: 'Base surge multiplier', detail: 'Always-on. e.g. 1.5× raises every fare in the zone by 50%.' },
          { label: 'Rush-hour window', detail: 'Optionally set a start→end hour (e.g. 17→21) and an extra rush-hour multiplier that applies only inside that window.' },
        ],
      },
      {
        title: '4 · Per-ride-type pricing overrides',
        steps: [
          { label: 'Open the zone', detail: 'Zone detail page → "Zone Pricing Overrides".' },
          { label: 'Pick a ride type', detail: 'Auto, Cab, Bike… each row shows the current global price.' },
          { label: 'Override or clear', detail: 'Set a zone Base Fare / Per-Km. Leave a field blank to fall back to the global ride-option price.' },
        ],
      },
      {
        title: '5 · Subscription plan overrides',
        steps: [
          { label: 'Open Subscriptions', detail: 'On the zone detail page.' },
          { label: 'Override a plan', detail: 'Change a plan’s price or ride limit just for this zone.' },
          { label: 'Or hide a plan', detail: 'Turn availability off to remove a global plan in this zone only.' },
        ],
      },
      {
        title: '6 · Map providers (advanced)',
        steps: [
          { label: 'Open Map Providers', detail: 'Zone detail page → "Configure Providers".' },
          { label: 'Override per zone', detail: 'Choose the Geocoding / Places / Routing provider for this zone.' },
          { label: 'Clear to reset', detail: 'Leave blank to fall back to the platform-default provider.' },
        ],
      },
    ],
    notes: [
      'Golden rule: any blank / cleared override falls back to the global default — global ride-option price, global subscription plan, or default map provider.',
      'Surge has two layers: the base multiplier (always on) plus an extra rush-hour multiplier during the configured hour window.',
      'The bulk "rename city" action updates the city name on every zone in that city at once.',
      'Permissions: view = zones:read; edit zone & surge = zones:write; pricing overrides = pricing:read / pricing:write; subscription overrides = subscriptions:read / subscriptions:write — so you may be able to see a zone but not its pricing section.',
    ],
  },

  staff: {
    title: 'Staff & Roles (RBAC) — how it works',
    intro: 'Create staff accounts and control exactly what each person can see and do.',
    flows: [
      {
        title: 'Add a staff member',
        steps: [
          { label: 'Create the account', detail: 'New staff → set username, password and role.' },
          { label: 'Assign regions', detail: 'Region/City roles only see data for their assigned cities.' },
          { label: 'Fine-tune access', detail: 'Optionally override individual permissions for that person.' },
        ],
      },
    ],
    notes: [
      '7 roles: SUPER_ADMIN, REGION_MANAGER, CITY_MANAGER, FIELD_AGENT, VERIFICATION_OPS, SUPPORT_AGENT, MARKETING.',
      'FIELD_AGENT has no web-dashboard access (mobile enrolment only).',
      'Permissions are enforced on the backend too — the UI just mirrors them. Requires staff:manage.',
    ],
  },

  liveops: {
    title: 'Live Ops — how it works',
    intro: 'Real-time monitoring of rides currently happening on the platform.',
    flows: [
      {
        title: 'Monitor active rides',
        steps: [
          { label: 'Watch the feed', detail: 'Active rides (requested → accepted → arrived → in progress) refresh automatically.' },
          { label: 'Spot problems', detail: 'Look for rides stuck in "requested" (no driver) for too long.' },
        ],
      },
    ],
    notes: [
      'This is read-only monitoring — drivers accept ride requests directly.',
      'Surge overrides are managed in Zones, not here.',
    ],
  },

  reports: {
    title: 'Reports & Exports — how it works',
    intro: 'Export operational data as CSV for finance, analysis or audits.',
    flows: [
      {
        title: 'Export a dataset',
        steps: [
          { label: 'Pick a date range', detail: 'Narrow the export to the period you need.' },
          { label: 'Choose a dataset', detail: 'Rides, Payments, Wallet transactions or Audit logs.' },
          { label: 'Download CSV', detail: 'The file downloads securely — no token ever appears in the URL.' },
        ],
      },
    ],
    notes: [
      'Exports are on-demand and capped at 50,000 rows per file.',
      'You only see datasets you have read permission for; region-scoped where applicable.',
    ],
  },

  audit: {
    title: 'Audit Log — how it works',
    intro: 'An immutable record of every admin action taken in the dashboard.',
    flows: [
      {
        title: 'Investigate an action',
        steps: [
          { label: 'Filter', detail: 'By actor, action type or date range.' },
          { label: 'Inspect', detail: 'See who did what, when, and the before/after values.' },
        ],
      },
    ],
    notes: [
      'Append-only — entries cannot be edited or deleted.',
      'Secrets (passwords, tokens) are automatically redacted. Requires audit:read.',
    ],
  },

  notifications: {
    title: 'Push Notifications — how it works',
    intro: 'Compose and send push notifications to app users.',
    flows: [
      {
        title: 'Send a notification',
        steps: [
          { label: 'Write the message', detail: 'Title and body — use the live preview to see how it looks.' },
          { label: 'Choose the audience', detail: 'Target specific users or broadcast.' },
          { label: 'Send', detail: 'The message is validated before it goes out.' },
        ],
      },
    ],
    notes: [
      'Sending is irreversible — double-check the audience and copy first.',
      'Requires the notifications:send permission.',
    ],
  },

  rideOptions: {
    title: 'Ride Options & Pricing — how it works',
    intro: 'Configure the ride types riders can choose and how each is priced.',
    flows: [
      {
        title: 'Edit pricing',
        steps: [
          { label: 'Open a ride option', detail: 'e.g. Auto, Cab, Bike.' },
          { label: 'Set the fare components', detail: 'Base fare, per-km and per-minute rates.' },
          { label: 'Save', detail: 'Changes flow into new fare estimates.' },
        ],
      },
    ],
    notes: [
      'Pricing changes affect what riders are quoted immediately.',
      'Requires the pricing:write permission.',
    ],
  },

  marketing: {
    title: 'Marketing Content — how it works',
    intro: 'Manage promotional content and explore items shown inside the app.',
    flows: [
      {
        title: 'Add or edit content',
        steps: [
          { label: 'Create / open an item', detail: 'New content, or click an existing one.' },
          { label: 'Fill the details', detail: 'Title, image and target.' },
          { label: 'Save', detail: 'It appears in the app once published.' },
        ],
      },
    ],
    notes: ['Requires the content:write permission.'],
  },

  cityOps: {
    title: 'City Operations — overview',
    intro: 'A launch console for a single city. The page is "context first, then actions": set who you are operating on, then run operations against that scope. Each section has its own "?" with a detailed flow.',
    flows: [
      {
        title: 'The standard launch workflow',
        steps: [
          { label: '1 · Operating Context', detail: 'Pick App Type + City. This scope drives every action below.' },
          { label: '2 · Launch Mode', detail: 'Flip ON for the city — makes offline drivers reachable + lets anyone accept free.' },
          { label: '3 · Invite drivers to go online (optional)', detail: 'Sends offline drivers a "go online" notification. It never marks anyone online — only the driver\'s own app does.' },
          { label: '4 · Dispatch Insights', detail: 'After ride requests come in, see exactly who each one reached.' },
        ],
      },
    ],
    notes: [
      'Launch Mode is the recommended switch (reversible, no database rows). Bulk Driver Activation writes real plan records, but only for drivers who have no active plan.',
      'Reach truth shows who we can actually reach (push confirmed, not answering, uninstalled…) and which ride requests went unfilled, by hour and pickup zone.',
      'Open the "?" on each section for its detailed flow diagram.',
      'All actions require the ops:bulk permission.',
    ],
  },

  cityOpsContext: {
    title: '1 · Operating Context — how it works',
    intro: 'The shared scope for the whole page. App Type + City here decide which drivers every operation below acts on — you set it once.',
    flows: [
      {
        title: 'Set the scope',
        steps: [
          { label: 'Pick App Type', detail: 'ApnaYatri or JustZapIt — drivers belong to one brand.' },
          { label: 'Pick State (filter)', detail: 'Just narrows the City dropdown — it is not part of the scope itself.' },
          { label: 'Pick Target City', detail: 'The city all actions run against. Must match the city on your Zone (e.g. "Lucknow").' },
          { label: 'Read the scope chips', detail: 'The chips show "City · App Type" and whether Launch Mode is ON/OFF for that city.' },
        ],
      },
      {
        title: 'Testing override (advanced)',
        steps: [
          { label: 'Click "Show advanced / testing override"', detail: 'Reveals the Test Phone field.' },
          { label: 'Enter a test phone', detail: 'e.g. your own driver number.' },
          { label: 'Run the invite or Bulk Activation', detail: 'They now target ONLY the driver with that phone, found by phone alone — City & App Type are ignored. An unknown phone shows "No driver with this phone number". The phone is cleared after the test run.' },
        ],
      },
    ],
    notes: [
      'Target City must equal your Zone\'s city name, or Launch Mode / Dispatch filtering won\'t line up.',
      'The scope chip turns blue once a valid city + app type (or a test phone) is set.',
      'Clearing the test phone returns every action to normal city-wide scope.',
    ],
  },

  cityOpsLaunchMode: {
    title: '2 · Launch Mode (Guaranteed Reach) — how it works',
    intro: 'The recommended launch switch. When ON for a city, a ride request reaches even offline drivers, and any driver can accept without an active plan — perfect for the early days when few drivers are subscribed or online.',
    warning: 'This changes live dispatch behaviour for the whole selected city. It is instantly reversible and writes no database rows.',
    flows: [
      {
        title: 'Turn it on',
        steps: [
          { label: 'Select the city in Operating Context', detail: 'The toggle is disabled until a city is chosen.' },
          { label: 'Flip the switch ON', detail: 'The city is added to the active launch-cities list (shown as a green chip).' },
        ],
      },
      {
        title: 'What happens when a rider requests a ride',
        steps: [
          { label: 'Rider requests', detail: 'A ride is created for a pickup in the launch city.' },
          { label: 'Tier 1 — live drivers', detail: 'Drivers currently online near the pickup are notified instantly (socket + FCM).' },
          { label: 'Tier 2 — offline wake', detail: 'Drivers near the pickup by last-known location are woken with a high-priority FCM, even if the app was killed.' },
          { label: 'Driver accepts', detail: 'Any woken driver can accept without a subscription (the plan check is bypassed in launch cities).' },
        ],
      },
    ],
    notes: [
      'Per-city: turning it on for Lucknow does not affect other cities.',
      'Reversible: flip OFF and dispatch returns to normal (online-only) immediately — nothing to clean up.',
      'Uses each driver\'s last-known location, so a driver becomes reachable after they have been online at least once.',
      'Prefer this over Bulk Driver Activation unless you specifically need real subscription records.',
    ],
  },

  cityOpsForceOnline: {
    title: 'Invite drivers to go online — how it works',
    intro: 'Sends a "go online" notification to OFFLINE drivers in the selected city and app. It never changes anyone\'s status: a driver counts as online only when their own app goes online and keeps sending its location.',
    warning: 'Keep the message honest — never say riders are waiting unless they are. Quiet hours apply: during them nothing is sent.',
    flows: [
      {
        title: 'Send an invite',
        steps: [
          { label: 'Set the scope', detail: 'City + App Type in Operating Context (or a Test Phone for one driver).' },
          { label: 'Optional — write the title / message', detail: 'Leave blank for the default "Ready to drive?" text.' },
          { label: 'Click "Preview invite"', detail: 'Read-only: shows exactly how many drivers would get it and why the others are skipped.' },
          { label: 'Confirm "Send to N driver(s)"', detail: 'The notification goes out; nobody\'s status changes.' },
          { label: '"Check who came online"', detail: 'Counts invited drivers whose own app is online now (live presence or on-duty), not a server flag.' },
        ],
      },
      {
        title: 'Who is skipped',
        steps: [
          { label: 'Invited recently', detail: 'At most one invite per driver per INVITE_MIN_INTERVAL_HOURS (default 6 h).' },
          { label: 'Busy / "Not now"', detail: 'Drivers on a ride or holding a booking, and drivers who tapped "Not now" recently.' },
          { label: 'Already online, suspended, no driver-app token', detail: 'Plus old accounts with no app type.' },
          { label: 'Over the batch cap', detail: 'INVITE_MAX_PER_BATCH per click (default 1,000); least-recently-invited go first, the rest are next in line.' },
          { label: 'Over today\'s city cap', detail: 'INVITE_MAX_PER_CITY_DAY (default 3,000) invites per city per IST day across all clicks.' },
          { label: 'Wallet below the minimum', detail: 'Drivers whose wallet is under DRIVER_MIN_BALANCE cannot take rides, so they are not invited.' },
        ],
      },
    ],
    notes: [
      'Quiet hours (OFFLINE_WAKE_QUIET_START → END, IST) block the invite for the whole city; the test phone is exempt from quiet hours and the interval.',
      'With a Test Phone set, the driver is found by phone alone (city and app type ignored); an unknown phone shows "No driver with this phone number".',
      'Counts: drivers whose current or home city is the selected city, with the selected app type. "Notifications sent to Firebase" means Firebase accepted the push — not that the phone showed it.',
      'It does NOT grant a plan — pair it with Launch Mode if you want those drivers to accept for free.',
      'Tapping the invite opens the driver app; whether it goes online straight away depends on the driver app build.',
    ],
  },

  cityOpsBulk: {
    title: 'Bulk Driver Activation — how it works',
    intro: 'Grants a chosen subscription plan, free, ONLY to drivers in the city who have NO active plan. It never changes anyone\'s online status and never touches a wallet.',
    warning: 'Existing plans — paid or free — are never expired, replaced or shortened. Drivers who already have an active plan are simply skipped.',
    flows: [
      {
        title: 'Grant a plan',
        steps: [
          { label: 'Set the scope', detail: 'City + App Type in Operating Context (or a Test Phone for one driver).' },
          { label: 'Pick the plan', detail: 'Any interval works (daily, weekly, monthly, yearly); validity is the plan\'s own.' },
          { label: 'Click "Preview grant"', detail: 'A dry run: how many drivers would get it, the plan\'s end date, and why the others are skipped (already have a plan, suspended, no token, unknown app type).' },
          { label: 'Confirm "Grant to N driver(s)"', detail: 'Each eligible driver gets the plan (wallet not charged) and a notification with the plan\'s real name — no notification during quiet hours.' },
        ],
      },
    ],
    notes: [
      'Suspended and deleted drivers are skipped.',
      'A double click cannot grant twice: the same grant within 10 minutes is refused, and concurrent grants are serialised.',
      'With a Test Phone set, it runs for that single driver only, found by phone alone (city & app type ignored), and only if they have no active plan; an unknown phone shows "No driver with this phone number".',
      'Drivers without a driver-app push token get no plan. Drivers whose wallet is below the minimum get the plan but no push ("got the plan, no push").',
      'Counts: drivers whose current or home city is the selected city, with the selected app type.',
      'Difference vs Launch Mode: this creates real plan records; Launch Mode just waives the plan requirement with no rows.',
    ],
  },

  cityOpsPreview: {
    title: '3 · Reach Preview — how it works',
    intro: 'A dry run. It shows who a ride request at a given pickup WOULD reach right now — total count + the nearest drivers — without sending a single notification. Use it to confirm a request reaches drivers (and that it is NOT waking the whole city).',
    flows: [
      {
        title: 'Preview the reach',
        steps: [
          { label: 'Pick a city (Operating Context)', detail: 'The pickup point auto-fills to the city centre.' },
          { label: 'Optionally fine-tune the point', detail: 'Type a specific pickup lat/lng (e.g. Gomti Nagar) for a precise area.' },
          { label: 'Click "Preview Reach"', detail: 'Read-only — it sends NO push and changes nothing.' },
          { label: 'Read the result', detail: 'Total reach + Live / On duty (FCM) / Offline split + the nearest 25 drivers with distances.' },
        ],
      },
    ],
    notes: [
      'A request only targets drivers within the dispatch radius of the pickup (NOTIFY_NEARBY_DRIVERS_RADIUS_KM), capped at NOTIFY_NEARBY_DRIVERS_MAX_RESULTS — NOT every driver in the city.',
      'Offline drivers only appear here when Launch Mode is ON for the city; with it off you preview live and on-duty drivers only.',
      'The "What happens on a real ride here" box also shows whether the DEMAND NUDGE would fire — an honest "go online" ping to offline city drivers when the near-market is thin (no ride card, no Accept). Tune it in section 5 · Dispatch Tuning.',
      'Tier 1.5: an on-duty driver whose live connection lapsed (parked, app in background) is still rung by FCM with the same ride card, in every city. The preview counts them like a real ride does.',
      'Drivers who cannot accept (rider-app token, wallet below the minimum) are left out, as in a real ride (REACH_SKIP_CANNOT_ACCEPT).',
      'This panel is a dry run: it sends nothing and burns no cooldown. The demand-nudge count already leaves out drivers in their cooldown, over the daily cap, busy or who tapped "Not now".',
      'Reach uses last-known driver locations, so a driver shows up once they have been online at least once.',
      'This is the predictive twin of Dispatch Insights (which shows what real past requests actually reached).',
    ],
  },

  cityOpsInsights: {
    title: '4 · Dispatch Insights — how it works',
    intro: 'For each recent ride request, see exactly how far it reached and what drivers did with it — your proof that requests are reaching drivers (including offline ones under Launch Mode).',
    flows: [
      {
        title: 'Read a row',
        steps: [
          { label: 'Reached', detail: 'Drivers the first dispatch targeted = Live + Woken. Re-dispatch waves are listed in the row\'s timeline.' },
          { label: 'Live', detail: 'Tier 1 (connected right now) plus Tier 1.5 (on duty but the connection lapsed — rung by FCM, not the socket).' },
          { label: 'On / Sil / Off', detail: 'Of those reached, how many were ONLINE, SILENT, or OFFLINE by their declared status.' },
          { label: 'FCM ok / fail', detail: 'Pushes accepted by Firebase vs failed (e.g. uninstalled app). Accepted ≠ guaranteed shown on screen.' },
          { label: 'Demand nudge', detail: 'If this ride found a thin near-market, how many OFFLINE city drivers got a separate "go online, demand is high" ping (no ride card, no Accept). "—" means it did not fire for this ride.' },
          { label: 'Accepted / Rejected / No reaction', detail: 'Who took it, who dismissed it, and how many simply did not respond (Reached − Accepted − Rejected).' },
        ],
      },
      {
        title: 'Filter & refresh',
        steps: [
          { label: 'Pick a city in Operating Context', detail: 'The table filters to that city (blank city = all cities).' },
          { label: 'Hit the Refresh icon', detail: 'Reloads the latest dispatches for the current filter.' },
        ],
      },
      {
        title: 'See exactly which drivers it reached',
        steps: [
          { label: 'Click the arrow on a ride row', detail: 'Expands a per-driver breakdown for that request.' },
          { label: 'Read the nearest-first list', detail: 'Up to 25 reached drivers, ordered by distance from the rider — each with name, phone, Live/Woken, status, distance, and a Reaction tag.' },
          { label: 'Reaction column', detail: 'Per driver: Accepted (the one who took it — also named in the main "Accepted" column), Rejected (they dismissed it), or No reaction (reached but never responded). This is the "who did NOT react" answer, by name.' },
        ],
      },
    ],
    notes: [
      'Data is recent only (kept ~6 hours) — this is a live operations view, not long-term analytics.',
      '"Woken" means reached only by the wake push; the server cannot tell "app killed" from "app backgrounded", so it is not split.',
      'The expanded list is capped at the 25 nearest reached drivers; the "Reached" number is the true total. How many can be reached per request is also bounded by the NOTIFY_NEARBY_DRIVERS_MAX_RESULTS config (tune it in section 5 · Dispatch Tuning).',
      'Distance uses each driver\'s last-known location, so a driver appears once they have been online at least once.',
      'Trigger a real ride request from a rider device, then Refresh to see the row appear.',
    ],
  },

  cityOpsTuning: {
    title: '5 · Dispatch Tuning & Demand Nudge — how it works',
    intro:
      'These are the dials behind two things: (1) GUARANTEED REACH — making sure a real ride finds drivers, and (2) the DEMAND NUDGE — an honest "go online, it is busy" ping to offline drivers when a real ride could not find anyone nearby. They are platform-wide and take effect within ~5 minutes.',
    warning:
      'The Demand Nudge is NOT a ride offer. It has no ride card and no Accept button — it only says "demand is high, go online". We deliberately refuse to send fake ride offers, because a driver who keeps getting rides they cannot take learns to ignore the real ride alert.',
    flows: [
      {
        title: 'A · Guaranteed reach — the two real-ride dials',
        steps: [
          { label: 'Dispatch radius (km)', detail: 'NOTIFY_NEARBY_DRIVERS_RADIUS_KM. How far from the pickup a real ride looks for drivers (both online Tier-1 and offline Tier-2). If "rides are not reaching enough drivers", WIDEN this (e.g. 2 → 5). This is the correct lever for a thin market.' },
          { label: 'Dispatch fan-out cap', detail: 'NOTIFY_NEARBY_DRIVERS_MAX_RESULTS. The most drivers one ride will ring. 100 is plenty for a city launch.' },
        ],
      },
      {
        title: 'B · Demand nudge — the four nudge dials',
        steps: [
          { label: 'ON / OFF', detail: 'DEMAND_NUDGE_ENABLED. 1 = on, 0 = off. Start at 0; turn on once Launch Mode is working.' },
          { label: 'Fire when near drivers are fewer than', detail: 'DEMAND_NUDGE_MIN_NEAR_REACH. The nudge only fires when a real ride finds FEWER than this many near drivers. 3 = fire when there are 0, 1, or 2 near drivers. Lower it to 1 to only nudge when literally nobody was near; raise it to nudge more eagerly.' },
          { label: 'Per-driver cooldown (hours)', detail: 'DEMAND_NUDGE_COOLDOWN_HOURS. A driver gets at most ONE nudge in this many hours, no matter how many thin-market rides happen. Default 3.' },
          { label: 'Max drivers per nudge', detail: 'DEMAND_NUDGE_MAX_RESULTS. Safety cap on how many offline drivers a single nudge pings. Default 200.' },
        ],
      },
      {
        title: 'C · Tier-3 city-wide ride wake — explained like you are five (KEEP IT 0)',
        steps: [
          { label: 'What "near" drivers are', detail: 'When a ride is requested, we first ring drivers NEAR the pickup: ones online now (Tier 1) and offline ones whose last-known spot is close (Tier 2). That is the good, targeted reach.' },
          { label: 'What Tier 3 would do', detail: 'CITY_ANCHOR_MIN_REACH turns on a THIRD wave that rings EVERY driver merely REGISTERED to the city — even a driver sitting 400 km away, even one we have no location for at all — and it sends them the REAL ride card.' },
          { label: 'Why that is bad', detail: 'A driver 400 km away cannot take the ride, but their phone rings like a real job. Do that a few times and they mute or distrust the ride alert. We literally saw this: ride #221 rang 101 drivers, most of them far away and useless. That is noise, not reach.' },
          { label: 'So we keep it 0', detail: 'CITY_ANCHOR_MIN_REACH = 0 means "Tier 3 never fires" (the gate is "fire when near-reach is below this number", and nothing is below 0). The honest DEMAND NUDGE replaces it: instead of a fake ride card to far drivers, we send a no-card "go online, it is busy" message.' },
        ],
      },
      {
        title: 'D · If you ever change CITY_ANCHOR_MIN_REACH — what each value does',
        steps: [
          { label: 'Set it to 0 (recommended)', detail: 'Tier 3 is OFF. Real rides ring only genuinely-near drivers. Far drivers are reached — honestly — by the Demand Nudge instead. Leave it here.' },
          { label: 'Set it to e.g. 3', detail: 'Now, whenever a real ride finds FEWER than 3 near drivers, Tier 3 fires and blasts the REAL ride card to up-to-100 drivers registered to the city, distance ignored. Expect "why did my phone ring for a ride 50 km away" complaints. Only do this if you truly want a city-wide ride blast and accept the noise.' },
          { label: 'Set it high (e.g. 100)', detail: 'Tier 3 fires on almost every ride (any ride with under 100 near drivers). This is what caused the ride #221 "rang 101 drivers" incident. Do not do this.' },
          { label: 'The safety net if you do enable it', detail: 'A far Tier-3 driver can be woken but CANNOT steal the ride: at accept time we check they are within ACCEPT_MAX_DISTANCE_KM (3 km) of the pickup, else it is blocked. But the phone still rang — which is the problem the Demand Nudge avoids.' },
        ],
      },
    ],
    notes: [
      'Golden rule: to reach MORE drivers for real rides, widen NOTIFY_NEARBY_DRIVERS_RADIUS_KM — do NOT raise CITY_ANCHOR_MIN_REACH.',
      'DEMAND_NUDGE_MAX_PER_DAY (default 2): at most this many demand nudges per driver per IST day, on top of the cooldown; 0 = no daily cap. Drivers are rotated (least recently nudged first) so everyone gets a turn.',
      'Ghost cleanup: GHOST_RECONCILE_ENABLED 0 = report only (logs how many drivers are marked online with no live presence); 1 = set them OFFLINE so nudges reach them again. GHOST_RECONCILE_IDLE_MIN (default 30, minimum 15) = minutes with no location and no update before a driver counts as a ghost. A driver whose app is online, on duty or on a ride is never touched.',
      'Go-online invites: INVITE_MIN_INTERVAL_HOURS (default 6) = at most one invite per driver per this many hours; INVITE_MAX_PER_BATCH (default 1000, max 5000) = drivers per click, least-recently-invited first.',
      'REACH_SKIP_CANNOT_ACCEPT (default 1): never ring or nudge a driver who cannot accept — rider-app token or wallet below DRIVER_MIN_BALANCE.',
      'REDISPATCH_STALE_BAND_ENABLED (default 0): waves also send the gentle ride-context nudge to near-but-stale drivers (needs OFFLINE_WAKE_STALE_RECENCY_MIN > 0). Never a loud ring.',
      'APP_OPEN_LOCATION_ENABLED (default 1): the GPS fix the app sends when opened refreshes the driver\'s last-known location (at most once per 3 min); it never marks them online.',
      'Reach Truth tab: PUSH_ACK_ENABLED (push receipts), PUSH_ACK_MIN_APP_VERSION (first build that sends them), PHONE_HEALTH_ENABLED (phone-health reports), REACH_SEGMENTS_ENABLED (nightly 04:00 IST snapshot), REACH_ACTIVE_DAYS (the "Reachable" window). All start OFF / safe; see the Reach truth "?" for what each number means.',
      'Channels tab — WhatsApp: WHATSAPP_DRIVER_ENABLED (daily 11:00 IST health + reinstall messages), WHATSAPP_DRIVER_DEMAND_ENABLED (demand alert to near drivers whose push isn\'t landing), WHATSAPP_DRIVER_MAX_PER_CITY_DAY, WHATSAPP_DRIVER_MIN_INTERVAL_DAYS, WHATSAPP_DRIVER_FIX_LINK, WHATSAPP_DRIVER_PLAY_LINK. Only drivers who opted in (app or agent) are ever messaged; needs Meta-approved templates.',
      'Channels tab — phone-call pilot: IVR_PILOT_ENABLED, IVR_PILOT_ZONE_IDS, IVR_NO_ACCEPT_SECONDS, IVR_MAX_CALLS_PER_RIDE, IVR_MAX_PER_DRIVER_DAY, IVR_MAX_PER_CITY_DAY, IVR_CANDIDATE_RADIUS_KM, IVR_CANDIDATE_MAX_AGE_MIN. Pressing 1 re-rings the app and texts the ride details — it never assigns the ride; the first driver to tap Accept gets it. Until a provider is set, calls are only logged (dry run).',
      'Channels tab — WHATSAPP_REINSTALL_MAX_PER_DRIVER (default 3; lifetime cap on "reinstall" messages per driver), DRIVER_CHANNEL_APP_TYPES (default ["ApnaYatri","unknown"]: which brands SMS / WhatsApp / calls may contact), REACH_PUBLIC_BASE_URL (domain used for the uninstall-reason link). Rider-typed text is never sent as-is: the zone name is used, or the text only after a safety filter. Drivers can stop WhatsApp messages from the link page.',
      'Channels tab — WINBACK_LIST_ENABLED: shows agents a "Win-back calls" list of their dark drivers in the Manager App, with call outcomes. No payouts.',
      'The Demand Nudge needs Launch Mode ON for the city AND DEMAND_NUDGE_ENABLED = 1. It only fires off a REAL ride that found a thin near-market — never on a timer, so the "demand is high" is always true.',
      'Use Reach Preview to see, for any pickup, whether a demand nudge would fire and how many drivers it would reach — without sending anything.',
      'Empty / unset key = the default shown under each field is used. Saving writes the value; it is read back within ~5 minutes everywhere.',
      'All edits require the ops:bulk / config write permission.',
    ],
  },

  cityOpsReachTruth: {
    title: 'Reach truth — how it works',
    intro: 'A nightly (04:00 IST) snapshot of how many drivers we can actually reach, why the rest are dark, what is wrong with their phones, and which ride requests went unfilled. It reads a stored snapshot, so opening it costs nothing.',
    warning: '"Recompute now" walks every driver — use it sparingly. It runs at most once every 10 minutes for everyone; a second click inside that window is refused.',
    flows: [
      {
        title: 'The driver segments (every driver is in exactly one)',
        steps: [
          { label: 'Reachable', detail: 'The app showed life in the last REACH_ACTIVE_DAYS days: it confirmed a push or sent a location.' },
          { label: 'Not answering', detail: 'We sent something this app build answers automatically (a silent location check, or any push once receipts are on) and it never answered. Invites never count.' },
          { label: 'Not yet proven', detail: 'The app build can answer, but we have not asked it within the window (or asked less than an hour ago).' },
          { label: 'Old app', detail: 'Silent for the whole window on a build that can neither confirm a push nor answer a location ping — we cannot tell alive from dead.' },
          { label: 'Uninstalled', detail: 'Firebase told us their push token is dead (recorded only from the token-hygiene release on).' },
          { label: 'Never opened the app', detail: 'No sign the app was ever installed: no push token, no app version and no location ever reported.' },
          { label: 'No push token', detail: 'Had the app once (a token, app version or location was seen) but has no push token now, with no recorded uninstall.' },
        ],
      },
      {
        title: 'Read the panels',
        steps: [
          { label: 'Arrows (↑ / ↓)', detail: 'Change against the snapshot about a week earlier; they appear after 7 days of snapshots.' },
          { label: 'Ride requests by hour', detail: 'Last 30 days by IST hour: accepted (green), expired unaccepted (red), cancelled after 60 s of searching (orange), cancelled within 60 s — the rider changed their mind (grey), outcome not recorded (purple: still marked searching an hour later), still searching (light grey). Red + orange = unfilled — the hours to bring drivers online.' },
          { label: 'By pickup zone', detail: 'The same counts per pickup zone; "Unfilled" is expired + cancelled after 60 s, as a share of requests.' },
          { label: 'Trend arrows', detail: 'Shown only when the week-old snapshot was built with the same rules (push receipts on/off, receipt build, ping build, Reachable window). After a rule change the panel says "rules changed since …, no comparison" for a week.' },
          { label: 'Phone health', detail: 'From phones that reported in the last 30 days: muted ride alerts, battery saver, background location, overlay, full-screen, Android restrictions — and by phone maker. A row reads "not measured in this app release" when no report carried that check.' },
          { label: 'Still uninstalled — by the week the app was removed', detail: 'Only drivers who are still uninstalled: a reinstall clears the record, so past weeks shrink as drivers come back. A removal is noticed when a push finds the token dead, so it shows up only after we next push that driver.' },
          { label: 'Counts', detail: 'Drivers: current city, else home city, else the city of their last location; the App Type selector is not applied. Ride requests: the pickup city recorded at dispatch.' },
        ],
      },
    ],
    notes: [
      'Needs REACH_SEGMENTS_ENABLED = 1 for the nightly run. Push receipts need PUSH_ACK_ENABLED and PUSH_ACK_MIN_APP_VERSION; phone health needs PHONE_HEALTH_ENABLED and the driver-app build that sends it.',
      'Snapshots are written for Launch-Mode cities and fleet-wide only.',
      'A stored push token is not proof the app is installed — this panel exists because the Supply Health "Token stored" number cannot tell.',
    ],
  },

  driverPayouts: {
    title: 'Driver Payouts (UPI) — overview',
    intro:
      'Drivers add their UPI ID in the app; you use it here to pay them (bonuses, incentives, settlements) through RazorpayX. This page is where you see coverage, export the payout file, and correct a driver\'s UPI if needed. The heavy lifting — actually sending money — is a deliberate manual step you do on the RazorpayX dashboard.',
    flows: [
      {
        title: 'The lifecycle: collect → reward → pay',
        steps: [
          { label: '1 · Driver adds their UPI', detail: 'In the app: Profile → "UPI for Payments" (or the side drawer). They confirm the ID in a read-back dialog before it saves.' },
          { label: '2 · You grant reward coins', detail: 'Coins (1 coin = ₹1) are points you give drivers — bulk by city or per-driver. They are NOT money the driver can withdraw; they only become cash when you pay.' },
          { label: '3 · You run a payout batch', detail: 'The "Reward payouts (coins → cash)" panel: create a batch, download its CSV (RazorpayX or Cashfree template), upload it on that payout dashboard, then Mark paid. Open the "?" on that panel for the full step-by-step.' },
          { label: '4 · Money lands + driver told', detail: 'On Mark paid, coins are debited and each driver is notified "N coins → ₹N sent to your UPI".' },
        ],
      },
      {
        title: 'What you can do on this page',
        steps: [
          { label: 'Read coverage at a glance', detail: 'KPI cards: active drivers, how many added a UPI (+ %), how many are still missing it, and how many changed it in the last 7 days.' },
          { label: 'Export for payout', detail: '"RazorpayX CSV" (upload-ready) or "Coverage CSV" (a plain list to eyeball who is covered).' },
          { label: 'Find & fix a driver', detail: 'Search / filter the table, click a row for their detail page, or use the pencil to edit their UPI and read its change history.' },
        ],
      },
    ],
    notes: [
      'Nothing is auto-charged — you always review and approve every batch on RazorpayX before money moves.',
      'Every UPI change (by a driver or by you) is journaled with who and when: a payout destination is a money target, so it is fully auditable for disputes.',
      'Emergency stop: set the App Config key UPI_COLLECTION_ENABLED to 0 to freeze all driver-side UPI edits (drivers see a "temporarily unavailable" banner); reading still works.',
      'Viewing needs drivers:read; editing a driver\'s UPI needs drivers:write.',
    ],
  },

  driverPayoutsExport: {
    title: 'Pay drivers via RazorpayX — full flow',
    intro:
      'This page only COLLECTS each driver\'s UPI ID. Sending money is a deliberate manual step: you export a ready-made file here, fill in the amounts, and upload it on the RazorpayX dashboard. Nothing is charged automatically — you always review and approve the batch on RazorpayX before any money moves.',
    warning:
      'Real money moves at step 3. Check two things every time: (1) the AMOUNT column, and (2) that the amount UNIT (Rupees vs Paise) matches your RazorpayX template — on a Paise template "50" means ₹0.50 and you must write "5000" for ₹50.',
    flows: [
      {
        title: 'One-time setup (do this once)',
        steps: [
          { label: 'Add your RazorpayX account number', detail: 'Settings → App Config → set RAZORPAYX_ACCOUNT_NUMBER to your RazorpayX current-account number. It then auto-fills the first column of every export. (Leave it blank to type it into the sheet each time.)' },
          { label: 'Check your template\'s amount unit', detail: 'On the RazorpayX dashboard, use "Download Template" once and see whether amounts are in Rupees or Paise. Set the "Template amount unit" dropdown here to match. Most accounts use Rupees; some bank-white-label accounts use Paise.' },
          { label: 'Fund the RazorpayX account', detail: 'Payouts draw from your RazorpayX balance — top it up before uploading a batch, or payouts queue/fail.' },
        ],
      },
      {
        title: '1 · Export the file (here)',
        steps: [
          { label: 'Optional — narrow the batch', detail: 'Set a City filter to pay just one city; leave it blank to include every driver who has a UPI.' },
          { label: 'Optional — prefill an amount', detail: 'Enter "Amount per driver" to put the same figure on every row, or leave it blank to type amounts in the sheet yourself.' },
          { label: 'Set purpose + narration', detail: 'Purpose (payout / salary / …) and the short narration that shows on the driver\'s bank statement (letters/digits, trimmed to 30 chars).' },
          { label: 'Click "RazorpayX CSV"', detail: 'Downloads a file already in RazorpayX\'s exact bulk-payout format — one row per driver with a UPI. (Use "Coverage CSV" instead for a plain human-readable list.)' },
        ],
      },
      {
        title: '2 · Fill in the amounts',
        steps: [
          { label: 'Open the CSV', detail: 'In Excel or Google Sheets.' },
          { label: 'Set the "Payout Amount" column', detail: 'Type what each driver should get (or adjust the prefilled figure) — mind the Rupees/Paise unit you picked.' },
          { label: 'Delete rows you are NOT paying', detail: 'Only the rows left in the file get paid. Remove anyone not in this batch.' },
        ],
      },
      {
        title: '3 · Upload & approve on RazorpayX',
        steps: [
          { label: 'Go to the RazorpayX dashboard', detail: 'Payouts → Bulk Payouts → Upload File (exact labels can vary by account).' },
          { label: 'Upload the CSV', detail: 'RazorpayX validates every row and flags problems (bad VPA, missing amount). Fix flagged rows and re-upload if needed.' },
          { label: 'Review the batch total', detail: 'Check the number of payouts and the TOTAL amount before you approve — this is your last stop before money moves.' },
          { label: 'Approve', detail: 'Depending on your RazorpayX setup this may need a second approver (maker-checker). Once approved, RazorpayX sends money to each driver\'s UPI.' },
        ],
      },
      {
        title: '4 · Confirm & reconcile',
        steps: [
          { label: 'Watch payout statuses on RazorpayX', detail: 'Each payout moves processing → processed (or reversed if the VPA failed).' },
          { label: 'Trace back to a driver', detail: 'Every row carries a Payout Reference Id of drv-<driverId>-<date>, so any payout maps to one driver and one batch date.' },
          { label: 'Handle failures', detail: 'If a payout reverses (wrong/closed UPI), ask that driver to re-check their UPI in the app, then include them in the next batch.' },
        ],
      },
    ],
    notes: [
      'The file matches RazorpayX\'s template exactly: UPI mode, fund-account type "vpa", and the bank-only IFSC / account-number columns are left intentionally blank.',
      'Contact Reference Id is drv-<driverId> (stable), so RazorpayX reuses the same contact for a driver across batches instead of creating duplicates.',
      'Region-scoped staff export only their own cities automatically.',
      'A driver with no UPI is simply absent from the file — chase them using the "Missing UPI" filter in the table below.',
      'Each export is capped at 50,000 rows; for very large runs, split by city.',
      'Exporting needs drivers:read — it never moves money by itself; approval always happens on RazorpayX.',
    ],
  },

  driverPayoutsCoins: {
    title: 'Paying drivers (coins → cash) — full flow',
    intro:
      'Drivers hold reward COINS (1 coin = ₹1) that you grant. Coins are not a wallet — they turn into real money only when you run a payout batch here and mark it paid. You download a file, upload it to RazorpayX or Cashfree, then confirm.',
    warning:
      'Coins are debited only when you click "Mark paid" — do that ONLY after the RazorpayX/Cashfree transfers have actually gone through. Mark paid is the point of no return: it burns coins and notifies drivers.',
    flows: [
      {
        title: 'One-time setup',
        steps: [
          { label: 'Set your RazorpayX account', detail: 'Payout settings above → RazorpayX account number + amount unit. Make sure your RazorpayX balance is funded.' },
        ],
      },
      {
        title: '1 · Give drivers coins',
        steps: [
          { label: 'Filter (optional)', detail: 'Set Current/Home city in the table filters to target a group; leave blank for everyone.' },
          { label: 'Grant coins', detail: '"Grant coins" → e.g. 10 → confirm. Every matched driver\'s coin balance goes up by 10. (Or grant per-driver from the pencil dialog.)' },
        ],
      },
      {
        title: '2 · Create a payout batch',
        steps: [
          { label: 'Set coins per driver', detail: 'In "Reward payouts", enter e.g. 10 and Create batch. It snapshots every driver with a UPI and coins, each getting min(10, their balance).' },
          { label: 'Nothing is charged yet', detail: 'The batch is a DRAFT — no coins are debited at this point.' },
        ],
      },
      {
        title: '3 · Pay on RazorpayX or Cashfree',
        steps: [
          { label: 'Download the batch CSV', detail: 'From the batch row (CSV ▾) or its detail — pick "RazorpayX CSV" or "Cashfree CSV" depending on which dashboard you will upload to. The per-driver amounts are already filled.' },
          { label: 'Upload it', detail: 'RazorpayX: dashboard → Bulk Payouts → upload. Cashfree: Payouts dashboard → Bulk Transfer → upload (UPI mode file). Review the total → approve. The provider sends the money to each UPI.' },
        ],
      },
      {
        title: '4 · Mark paid (the commit)',
        steps: [
          { label: 'Confirm it went through', detail: 'Only after RazorpayX shows the transfers processed.' },
          { label: 'Click "Mark paid"', detail: 'NOW coins are debited, journaled, and each driver is notified "10 coins → ₹10 sent". The batch becomes Paid.' },
          { label: 'Handle rejects', detail: 'If RazorpayX rejected a row (bad UPI), mark it failed — no coins are taken; include that driver in a later batch.' },
        ],
      },
    ],
    notes: [
      'Worked example — pay ₹10 to Lucknow: filter Current city = Lucknow → Grant coins 10 → Create batch (coins per driver 10) → download CSV → upload + approve on RazorpayX → once processed, open the batch → Mark paid. Each Lucknow driver: coins 10 → 0, ₹10 in their bank, push notification sent.',
      'Coins per driver is capped at each driver\'s balance — a driver with 6 coins gets ₹6, not ₹10.',
      'Coins are debited at Mark paid, never at Create — a failed or cancelled batch costs a driver nothing.',
      'The batch CSV contains only UNPAID rows (that is the double-payment guard) — once a batch is paid or cancelled its CSV would be empty, so the download is blocked with a note. Use "View" for the full row list.',
      'A driver needs a saved UPI AND coins > 0 to appear in a batch. Chase missing UPIs with the "Missing UPI" filter.',
      'Granting and paying coins needs the wallet:adjust permission.',
    ],
  },

  driverPayoutsManage: {
    title: 'Edit a driver\'s UPI & history — how it works',
    intro:
      'Sometimes you need to set or correct a driver\'s UPI for them (e.g. they read it out on a support call), or review who last changed it. The pencil icon on each row opens this dialog.',
    warning:
      'You are editing a money destination. Confirm the ID with the driver directly (read it back) before saving — every change is journaled under your name.',
    flows: [
      {
        title: 'Edit or clear a UPI',
        steps: [
          { label: 'Open the row', detail: 'Click the pencil on a driver in the table.' },
          { label: 'Type the correct UPI', detail: 'Format name@bank (e.g. 9876543210@ybl). The field checks the shape live.' },
          { label: 'Save — or clear', detail: 'Save to update; or leave the field empty and Save to remove the UPI entirely.' },
        ],
      },
      {
        title: 'Review the change history',
        steps: [
          { label: 'Read the journal', detail: 'The dialog lists every change: old → new value, when, and by whom — the driver in-app, or which admin.' },
          { label: 'Use it for disputes', detail: 'If a driver says a payout went to the wrong place, the history shows exactly when and by whom the UPI was last changed.' },
        ],
      },
    ],
    notes: [
      'Editing here needs drivers:write; without it you can still open the dialog to READ the history.',
      'Re-saving the same value is a no-op (no duplicate journal entry).',
      'This is the ONLY admin path that can change a UPI — the generic driver-edit screen deliberately cannot.',
    ],
  },

  support: {
    title: 'Support Tickets — how the system works',
    intro:
      'This is the customer-support desk. Riders and drivers raise tickets from the app (fare disputes, lost items, payout problems, safety, and more). Your job is to pick up a ticket, talk to the customer in its live chat, and move it to Resolved within its time target (SLA). This guide is everything you need — no one has to train you.',
    warning:
      'SAFETY tickets (priority "P0 · Safety", red) come first, always. They are auto-created and everyone is paged. Stop what you are doing, open it, and if the person is in danger tell them to use the in-app SOS / call local emergency services, then escalate to your manager.',
    flows: [
      {
        title: 'A · Your daily loop (what an agent does)',
        steps: [
          { label: '1 · Open the queue', detail: 'Support → Tickets. Newest first. Filter by Status, Issue Type or Priority. P0/Urgent (red/orange) are the ones to grab first.' },
          { label: '2 · Claim the ticket', detail: 'Open a ticket and click "Claim". It becomes yours so two agents never work the same one. If someone already claimed it you\'ll see a notice — pick another.' },
          { label: '3 · Read the context', detail: 'The detail page shows the full chat, the customer, and the linked ride (fare, route). Read before you reply.' },
          { label: '4 · Reply in the chat', detail: 'Type in the reply box; the customer sees it live in their app. Your first reply also starts the "first response" clock — reply promptly.' },
          { label: '5 · Move the status', detail: 'In Progress while you work it; Pending Customer if you are waiting on them.' },
          { label: '6 · Resolve', detail: 'When the issue is handled, set status to Resolved. That closes the chat and stops the SLA clock.' },
        ],
      },
      {
        title: 'B · The ticket lifecycle (statuses)',
        steps: [
          { label: 'New', detail: 'Just created by the customer, no agent yet. Grab it.' },
          { label: 'Open', detail: 'Claimed / being triaged.' },
          { label: 'In Progress', detail: 'You are actively working it (replying, investigating).' },
          { label: 'Pending Customer', detail: 'You replied and are waiting on the customer. The resolution clock keeps running.' },
          { label: 'Resolved', detail: 'Issue handled. Chat closes; SLA stops. Sets the "resolved" time.' },
          { label: 'Closed', detail: 'Fully finished / archived. A resolved ticket the customer re-opens goes back to Open.' },
        ],
      },
      {
        title: 'C · Priority & SLA — how fast you must act',
        steps: [
          { label: 'P0 · Safety', detail: 'Assault, threats, accident, "unsafe". Respond within ~5 minutes. Drop everything.' },
          { label: 'Urgent', detail: 'Escalated / time-critical. Respond fast.' },
          { label: 'High', detail: 'Driver payout / earnings / cash disputes — respond within ~1 hour.' },
          { label: 'Normal', detail: 'Fare, app, general — respond within a few hours.' },
          { label: 'Low', detail: 'Minor / informational.' },
        ],
      },
      {
        title: 'D · When you get an SLA-breach alert',
        steps: [
          { label: 'What it means', detail: 'A ticket blew past its response or resolution target. The system auto-bumps its priority and pings the desk — it is now more urgent.' },
          { label: 'What to do', detail: 'Pick it up now. If it is not yours, claim or ask your manager to reassign it. Reply to the customer to stop the first-response breach.' },
        ],
      },
      {
        title: 'E · Assign, reassign & find a ticket',
        steps: [
          { label: 'Claim (agents)', detail: 'Takes an unassigned ticket. First click wins — no collisions.' },
          { label: 'Assign / reassign (managers)', detail: 'Managers can hand a ticket to a specific agent, or unassign it back to the queue.' },
          { label: 'Search', detail: 'Use search to find a ticket by its number (AY-2026-…), the customer\'s phone, their id, or words in the description.' },
        ],
      },
    ],
    notes: [
      'Who does what: SUPPORT_AGENT — claim, reply, and resolve tickets (support:read + support:write). CITY_MANAGER — the same, but only for tickets in their assigned city, plus assign/reassign within their team. SUPER_ADMIN — everything, all cities.',
      'Region scoping: a city manager only sees tickets for their own city; a super-admin sees all.',
      'You cannot issue refunds from here — a fare/payout refund needs the Finance/Wallet screen (payments:write). Note it on the ticket and route it if you lack that permission.',
      'The bot: simple FAQ questions get an automatic reply before an agent is involved. The moment a ticket exists (a human is needed), the bot goes silent — you own the conversation.',
      'Safety is never handled by the bot: those tickets are P0, auto-paged, and always go to a human.',
      'Every action you take (claim, reply, status change, resolve) is recorded on the ticket\'s history for audit.',
      'Open the "?" on this page any time — this guide is everything you need to run the desk.',
    ],
  },
};
